import { compact, isEmpty, uniqBy } from "lodash";
import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { useEffect, useMemo } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { Calendar } from "react-widgets";
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip
} from "recharts";
import DotsFallback from "../../components/DotsFallback";
import CloseIcon from "../../components/icons/CloseIcon";
import InfoIcon from "../../components/icons/InfoIcon";
import { useFilteredList } from "../../context/filteredList";
import { useHotspots } from "../../context/hotspots";
import { useLoading } from "../../context/loading";
import { LoadingKey } from "../../context/loading/LoadingProvider";
import { Entity } from "../../utils/constants";
import useMapCenter from "../../utils/useMapCenter";
import useTimeTravel from "../../utils/useTimeTravel";

interface Props extends RouteComponentProps<{}> {}

const TimeTravel: React.FC<Props> = () => {
  const {
    getHotspotCoordinates,
    fetchHotspotAssertionsByHex,
    hotspotAssertionsMap
  } = useHotspots();
  const { getIsReady } = useLoading();
  const { filteredList } = useFilteredList();
  const centerInH3HexRes4 = useMapCenter((state) => state.centerInH3HexRes4);
  const date = useTimeTravel((state) => state.date);
  const timeTravelTimestamp = useTimeTravel((state) => state.timestamp);
  const setDate = useTimeTravel((state) => state.setDate);

  useEffect(() => {
    if (centerInH3HexRes4) {
      fetchHotspotAssertionsByHex(centerInH3HexRes4);
    }
  }, [centerInH3HexRes4, fetchHotspotAssertionsByHex]);

  useEffect(
    () => () => {
      setDate("");
    },
    [setDate]
  );

  const isLoading = useMemo(
    () => !getIsReady([LoadingKey.hotspotsAroundLocations]),
    [getIsReady]
  );

  const filteredHotspots = useMemo(
    () =>
      compact(
        filteredList[Entity.hotspot].filter(
          (hotspot) =>
            !!getHotspotCoordinates(hotspot.id, timeTravelTimestamp)?.h3Index
        )
      ),
    [filteredList, getHotspotCoordinates, timeTravelTimestamp]
  );

  const filteredHotspotsOwnerCount = useMemo(
    () => uniqBy(filteredHotspots, "owner").length,
    [filteredHotspots]
  );

  const selectedDateFormatted = useMemo(
    () =>
      moment(date ? new Date(date) : new Date())
        .utc()
        .format("D MMMM YYYY"),
    [date]
  );

  const chartData: {
    date: string;
    dateString: string;
    hotspotsCount: number;
    walletsCount: number;
  }[] = useMemo(() => {
    const today = moment(new Date()).utc();
    const chartDateFrom = moment(date ? new Date(date) : new Date())
      .utc()
      .startOf("month");

    const lastChartDate = chartDateFrom.clone().endOf("month");
    const currentDate = chartDateFrom.clone();

    const data = [];

    while (currentDate.isBefore(lastChartDate)) {
      const timestamp = currentDate.unix() + 24 * 60 * 60;

      const hotspots = compact(
        filteredList[Entity.hotspot].filter(
          (hotspot) => !!getHotspotCoordinates(hotspot.id, timestamp)?.h3Index
        )
      );

      data.push({
        date: currentDate.format("D MMMM YYYY"),
        dateString: currentDate.toString(),
        hotspotsCount: currentDate.isBefore(today) ? hotspots.length : 0,
        walletsCount: uniqBy(hotspots, "owner").length
      });

      currentDate.add(1, "days");
    }

    return data;
  }, [date, filteredList, getHotspotCoordinates]);

  return (
    <article className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="-mt-5">
        <div className="flex justify-end">
          <nav className="flex" aria-label="Close">
            <span className="relative z-0 shadow-sm rounded-md flex justify-between items-center">
              <Link
                to="/hotspots"
                className="-ml-px relative inline-flex items-center px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-hotspotty-500 focus:border-hotspotty-500 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700"
              >
                <CloseIcon className="h-5 w-5" />
              </Link>
            </span>
          </nav>
        </div>
        <div className="sm:flex-1 sm:min-w-0 sm:flex sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
          <div className="min-w-0 flex-1">
            <h1 className="text-md sm:text-2xl font-bold text-gray-900 dark:text-white truncate inline-flex items-center">
              <div className="flex-1 flex items-center">
                <span className="mr-2">Time travel</span>
                <Tooltip
                  placement="right"
                  overlay="Based on the (filtered) list of hotspots fetched for the purple zone around the center of the map."
                >
                  <InfoIcon className="inline-flex h-5 w-5" />
                </Tooltip>
              </div>
            </h1>
          </div>
        </div>
      </div>

      <div className="my-6">
        <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div className="bg-white shadow rounded-lg dark:bg-gray-800">
            <Calendar
              bordered={false}
              value={date ? new Date(date) : new Date()}
              max={new Date()}
              onChange={(value) => setDate(value.toString())}
              onNavigate={(value) => setDate(value.toString())}
              disabled={isEmpty(hotspotAssertionsMap)}
            />
          </div>

          <div className="bg-white shadow rounded-lg sm:col-span-2 dark:bg-gray-800">
            <div className="px-4 py-5 sm:p-6">
              <dt className="flex">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-500 truncate dark:text-gray-300">
                    Month
                  </div>

                  <div className="flex items-baseline mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                    {moment(date ? new Date(date) : new Date())
                      .utc()
                      .format("MMMM YYYY")}
                  </div>
                </div>

                <div className="flex-1 flex justify-center">
                  <div>
                    <div className="text-sm font-medium text-gray-500 truncate dark:text-gray-300">
                      Hotspots
                    </div>

                    <div className="flex items-baseline">
                      <DotsFallback
                        className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white"
                        loading={isLoading}
                      >
                        {filteredHotspots.length}
                      </DotsFallback>
                    </div>
                  </div>
                </div>

                <div className="flex-1 flex justify-end">
                  <div>
                    <div className="text-sm font-medium text-gray-500 truncate dark:text-gray-300">
                      Wallets
                    </div>

                    <div className="flex items-baseline">
                      <DotsFallback
                        className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white"
                        loading={isLoading}
                      >
                        {filteredHotspotsOwnerCount}
                      </DotsFallback>
                    </div>
                  </div>
                </div>
              </dt>

              <DotsFallback
                className="mt-1 text-3xl font-semibold text-gray-900"
                loading={false}
              >
                <div className="w-full h-28">
                  <ResponsiveContainer>
                    <BarChart data={chartData}>
                      <Bar
                        dataKey="hotspotsCount"
                        onClick={(entry) => setDate(entry.dateString)}
                      >
                        {chartData.map((entry, index) => (
                          <Cell
                            cursor="pointer"
                            key={`cell-${index}`}
                            fill={
                              selectedDateFormatted === entry.date
                                ? "#a96ceb"
                                : "rgba(170, 108, 235, 0.4)"
                            }
                          />
                        ))}
                      </Bar>
                      <RechartsTooltip
                        cursor={false}
                        content={({ active, payload }: any) => {
                          if (active && !isEmpty(payload)) {
                            const {
                              date,
                              dateString,
                              hotspotsCount,
                              walletsCount
                            } = payload[0].payload;

                            const isInFuture = moment()
                              .utc()
                              .isBefore(moment(dateString).utc());
                            if (isInFuture) return null;

                            return (
                              <div className="text-xs bg-white p-4 rounded-md shadow z-50 dark:bg-gray-800 dark:text-white">
                                <div className="font-medium">{date}</div>
                                <div>Hotspots: {hotspotsCount}</div>
                                <div>Wallets: {walletsCount}</div>
                              </div>
                            );
                          }

                          return null;
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </DotsFallback>
            </div>
          </div>
        </dl>
      </div>
    </article>
  );
};

export default TimeTravel;
